import { ref } from 'vue';
import { httpService, type ApiResponse } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { compressImage } from '../utils/compressImage';
import '../contracts/Stock_AdjustInput.ts';
import '../contracts/Stock_ReturnItemInput.ts';

import '../contracts/Stock_AdjustResponse';
import '../contracts/Stock_GetResponse';
import '../contracts/Stock_ListaResponse';
import '../contracts/Stock_LowStockResponse';
import '../contracts/Stock_MovementsResponse';
import '../contracts/Stock_ReturnItemResponse';
import type { Stock_AdjustInput } from '../contracts/Stock_AdjustInput';
import type { Stock_ReturnItemInput } from '../contracts/Stock_ReturnItemInput';
/**
 * Composable for SO Stock management operations.
 * Manages stock levels, adjustments, movements, and returns.
 */
export function useSOStock() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/stock';

  const fetchStock = async (franchiseId?: number) => {
    log('useSOStock', 'fetchStock', 'Fetching all stock', { franchiseId });
    isLoading.value = true;
    error.value = null;
    try {
      const url = franchiseId !== undefined
        ? `${basePath}/lista?franchise_id=${franchiseId}`
        : `${basePath}/lista`;
      const response = await httpService.get(url, { responseContract: 'Stock_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar estoque');
    } catch (err) {
      logError('useSOStock', 'fetchStock', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const getStock = async (productId: number, franchiseId?: number) => {
    log('useSOStock', 'getStock', 'Fetching stock for product', { productId, franchiseId });
    isLoading.value = true;
    error.value = null;
    try {
      const url = franchiseId
        ? `${basePath}/get/${productId}/${franchiseId}`
        : `${basePath}/get/${productId}`;
      const response = await httpService.get(url, { responseContract: 'Stock_GetResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar estoque do produto');
    } catch (err) {
      logError('useSOStock', 'getStock', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const adjustStock = async (productId: number, franchiseId: number, data: { quantity: number; notes?: string; so_stock_price?: number | null; so_stock_minimum_quantity?: number | null }) => {
    log('useSOStock', 'adjustStock', 'Adjusting stock', { productId, franchiseId, data });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Stock_AdjustInput = {
        franchise_id: franchiseId,
        quantity: data.quantity,
        notes: data.notes,
        product_id: productId,
      };
      if (data.so_stock_price !== undefined && data.so_stock_price !== null) {
        payload.so_stock_price = data.so_stock_price;
      }
      if (data.so_stock_minimum_quantity !== undefined && data.so_stock_minimum_quantity !== null) {
        payload.so_stock_minimum_quantity = data.so_stock_minimum_quantity;
      }
      validate('Stock_AdjustInput', payload);
      const response = await httpService.post(`${basePath}/adjust`, payload, { responseContract: 'Stock_AdjustResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao ajustar estoque');
    } catch (err) {
      logError('useSOStock', 'adjustStock', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchLowStock = async () => {
    log('useSOStock', 'fetchLowStock', 'Fetching low stock items');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/low_stock`, { responseContract: 'Stock_LowStockResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar itens com estoque baixo');
    } catch (err) {
      logError('useSOStock', 'fetchLowStock', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchMovements = async (productId: number) => {
    log('useSOStock', 'fetchMovements', 'Fetching stock movements', { productId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/movements/${productId}`, { responseContract: 'Stock_MovementsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar movimentações de estoque');
    } catch (err) {
      logError('useSOStock', 'fetchMovements', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };


  const returnItem = async (data: Stock_ReturnItemInput) => {
    log('useSOStock', 'returnItem', 'Returning item to stock', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('Stock_ReturnItemInput', data);
      const response = await httpService.post(`${basePath}/return_item`, data, { responseContract: 'Stock_ReturnItemResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao devolver item ao estoque');
    } catch (err) {
      logError('useSOStock', 'returnItem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchStock,
    getStock,
    adjustStock,
    fetchLowStock,
    fetchMovements,
    returnItem
  };
}
